import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
  tenantApiRoutes,
  NotificationResponse,
} from '@repo/contracts/tenant-api';

import { useCurrentUser } from '@/hooks/use-current-user';
import { useAuth } from '@/lib/auth';
import type { AppDispatch } from '@/redux/store';

import {
  notificationsApi,
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
} from '../api/notifications.api';

/**
 * Hook that provides notifications from RTK Query + real-time SSE updates.
 * - Fetches initial notifications via RTK Query (GET /notifications/users/:userId)
 * - Subscribes to SSE stream for real-time push notifications
 * - Provides markAsRead/markAllAsRead actions
 */
export function useNotificationStream() {
  const { currentUser } = useCurrentUser();
  const { accessToken } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const eventSourceRef = useRef<EventSource | null>(null);

  const userId = currentUser?.id;

  const { data: notifications = [], isLoading } = useGetNotificationsQuery(
    userId!,
    { skip: !userId },
  );

  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [markAllAsRead] = useMarkAllNotificationsAsReadMutation();

  // SSE connection for real-time notifications
  useEffect(() => {
    if (!userId || !accessToken) return;

    const baseUrl =
      import.meta.env.VITE_TENANT_API_SSE_URL ||
      import.meta.env.VITE_TENANT_API_URL ||
      'http://localhost:3012';
    const sseUrl = `${baseUrl}${tenantApiRoutes.notifications.sse(userId)}?token=${accessToken}`;

    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const notification: NotificationResponse = JSON.parse(event.data);
        // Update RTK Query cache with the new notification
        dispatch(
          notificationsApi.util.updateQueryData(
            'getNotifications',
            userId,
            (draft) => {
              // Avoid duplicates
              const exists = draft.some((n) => n.id === notification.id);
              if (!exists) {
                draft.unshift(notification as any);
              }
            },
          ),
        );
      } catch {
        // Ignore malformed SSE messages
      }
    };

    eventSource.onerror = () => {
      // EventSource will auto-reconnect by default
    };

    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [userId, accessToken, dispatch]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleMarkAsRead = useCallback(
    (id: string) => {
      markAsRead(id);
    },
    [markAsRead],
  );

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
  };
}
